'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiArticleLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { Beams } from '@/components/beams';
import { dur, spring, springFade, stagger } from '@/lib/motion';

/**
 * Blog dizininin açılışı — /features/agent hero'sunun AÇIK kardeşi.
 *
 * ── Aynı iskelet, ters ışık (kullanıcı kararı, 12 Ağu 2026) ───────────────
 *
 * Kurulum `agent-hero.tsx` ile birebir: header payı, bloğun tamamına yayılan
 * bir zemin katmanı, onun üstünde içerik sütunu ve ortalanmış bir metin
 * kutusu (rozet → iki satırlık başlık → cümle). Değişen iki şey var:
 *
 *   • Zemin AÇIK. Ajan sayfası `.dark-block` rampasıyla açılıyor; burada
 *     beyazdan çok açık bir nane grisine inen bir rampa var. Hat renkleri de
 *     buna göre: koyu yüzeyde `#ffffff20` olan çizgi burada `%12 siyah` —
 *     /about ve blog listesindeki hatlarla aynı mürekkep.
 *   • Işık kaynağı ışın alanı (`beams.tsx`), küre değil.
 *
 * Tipografi de ödünç (kullanıcı kararı, 12 Ağu 2026): başlık ve açıklama
 * anasayfanın hero sınıflarını (`.new-hero-*`) kullanıyor — ajan sayfasının
 * kullandığı sınıfların ta kendisi. Kopyalanmış bir ölçek değil AYNI ölçek:
 * 48px/500, satır arası 1.16, açıklama 17.4px. O sınıflar vendor CSS'ine
 * `[data-v-…]` seçicisiyle bağlı, bu yüzden nitelik markup'ta tekrarlanıyor
 * (aynı gerekçe: components/hero.tsx, components/agent-hero.tsx).
 *
 * 🔴 Renk tek yerde ayrılmak ZORUNDA: koyu sürümde üst satır gri→BEYAZ
 * rampası taşıyor, beyaz kâğıtta o satır görünmez olur. Üst satır bu yüzden
 * ters yöne akıyor (gri→siyah); alt satırın yeşili ajan sayfasındakiyle
 * birebir aynı duraklarda (globals.css → `.bhero .new-hero-title-line--*`).
 *
 * ── Neden buton yok ───────────────────────────────────────────────────────
 *
 * Ajan sayfasının hero'sunda iki buton var çünkü o sayfanın işi bir ürünü
 * satmak. Bir dizinin işi ise okutmak: buradaki sonraki adım hemen altındaki
 * liste, kayıt formu değil. Kapanış şeridi (`cta.tsx`) zaten sayfanın sonunda
 * duruyor ve tek birincil aksiyon orada kalıyor.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Bölüm ekranın tepesinde açılıyor; `whileInView` beklemesi anlamsız,
 * `animate` ile doğrudan oynuyor (aynı gerekçe: components/page-head.tsx).
 * Sıra okuma sırası: rozet → başlık → cümle. Blur yalnızca başlıkta.
 *
 * Işın alanının kendi döngüsü `<MotionConfig>`in kapsamı dışında; azaltılmış
 * hareket orada elle karşılanıyor (bkz. components/beams.tsx).
 */

/* Vendor CSS'inin kapsam niteliği. Anasayfanın hero sınıfları `[data-v-…]`
   seçicisine bağlı; nitelik olmadan kural hiç uygulanmıyor. */
const V = 'd0b8df6f';

const sceneV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose, delayChildren: 0.05 } },
};

const itemV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Bölümdeki tek blur: sayfanın adı. */
const titleV: Variants = {
  hidden: { opacity: 0, y: 14, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

/* Alan metinden önce değil onunla birlikte açılıyor: ölçek 0.96'dan geliyor,
   yani "büyüyerek beliriyor" değil "zaten oradaydı, ışığı açıldı". */
const beamsV: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  show: { opacity: 1, scale: 1, transition: springFade(spring.soft, dur.slow) },
};

export function BlogHero() {
  const t = useTranslations('blog.hero');
  const uid = useId();

  return (
    <section className="bhero" aria-labelledby={`${uid}-title`}>
      <div className="bhero-body">
        {/* Alan sütunun DIŞINDA, bloğun tamamına yayılı ve onun ALTINDA:
            içerik sütununun dikey hatları ışınların üstünde kalıyor. */}
        <motion.div className="bhero-beams" variants={beamsV} initial="hidden" animate="show">
          <Beams className="bhero-beams-canvas" />
        </motion.div>

        {/* Header `position: fixed`, akışta yer kaplamıyor — payı burada.
            Pay bloğun İÇİNDE, kardeşi değil: ışın alanı header'ın arkasından
            da geçiyor. Ayrık kabuk opak beyaz ve kendi gölgesini taşıyor
            (globals.css → `.header-island`), ışınların üstünde kayboluyor
            değil, yüzüyor. */}
        <div className="bhero-space" aria-hidden="true" />

        <div className="grid-12-container">
          <div className="grid-item-2fr" />

          <div className="grid-item-8fr">
            <div className="bhero-frame">
              <motion.div
                className="bhero-copy"
                variants={sceneV}
                initial="hidden"
                animate="show"
              >
                <motion.p className="bhero-eyebrow" variants={itemV}>
                  <RiArticleLine size={18} aria-hidden="true" />
                  {t('eyebrow')}
                </motion.p>

                <motion.h1
                  className="new-hero-title"
                  data-v-d0b8df6f={V}
                  id={`${uid}-title`}
                  variants={titleV}
                >
                  {/* İki satır da span: kırılma noktası viewport genişliğine
                      bağlı kalmıyor, her kademede iki satır. */}
                  <span
                    className="new-hero-title-line new-hero-title-line--lead"
                    data-v-d0b8df6f={V}
                  >
                    {t('titleLine1')}
                  </span>
                  <span
                    className="new-hero-title-line new-hero-title-line--accent"
                    data-v-d0b8df6f={V}
                  >
                    {t('titleLine2')}
                  </span>
                </motion.h1>

                <motion.p
                  className="new-hero-description"
                  data-v-d0b8df6f={V}
                  variants={itemV}
                >
                  {t('description')}
                </motion.p>
              </motion.div>
            </div>
          </div>

          <div className="grid-item-2fr" />
        </div>
      </div>
    </section>
  );
}
